import {NavLink, useLocation} from 'react-router-dom';
import {Badge, makeStyles, tokens} from '@fluentui/react-components';
import {
    Home24Regular,
    Calendar24Regular,
    ContactCard24Regular, Document24Regular, Form24Regular,
} from '@fluentui/react-icons';
import {useTranslation} from 'react-i18next';
import {useQuery} from "@tanstack/react-query";
import {getApiV1DocumentsUnreadCount} from "@api/endpoints/documents";

const useStyles = makeStyles({
    nav: {
        display: 'flex',
        flexDirection: 'column',
        padding: tokens.spacingVerticalM,
        gap: tokens.spacingVerticalXS,
    },
    section: {
        marginBottom: tokens.spacingVerticalM,
    },
    link: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        borderRadius: tokens.borderRadiusMedium,
        textDecoration: 'none',
        color: 'inherit',
        fontSize: tokens.fontSizeBase300,
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    linkActive: {
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground1,
        fontWeight: tokens.fontWeightSemibold,
        '&:hover': {
            backgroundColor: tokens.colorBrandBackground2Hover,
        },
    },
    icon: {
        fontSize: '20px',
        flexShrink: 0,
    },
    navLabel: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.spacingHorizontalS,
        width: '100%',
    }
});

interface NavItemProps {
    to: string;
    icon: React.ReactNode;
    label: React.ReactNode;
}

function NavItem({to, icon, label}: NavItemProps) {
    const styles = useStyles();
    const location = useLocation();
    const isActive = location.pathname === to || location.pathname.startsWith(`${to}/`);

    return (
        <NavLink
            to={to}
            className={`${styles.link} ${isActive ? styles.linkActive : ''}`}
        >
            <span className={styles.icon}>{icon}</span>
            <span>{label}</span>
        </NavLink>
    );
}

export function EmployeeNavMenu() {
    const styles = useStyles();
    const {t} = useTranslation('navigation');
    const unreadDocumentsQuery = useQuery({
        queryKey: ['documents', 'unread-count'],
        queryFn: () => getApiV1DocumentsUnreadCount(),
    });
    const unreadDocuments = Number(unreadDocumentsQuery.data?.count ?? 0);
    
    return (
        <nav className={styles.nav}>
            <div className={styles.section}>
                <NavItem to="/visitor-registration" icon={<Form24Regular/>}   label={t('visitorRegistration')} />
            </div>
            <div className={styles.section}>
                <NavItem to="/" icon={<Home24Regular/>} label={t('dashboard')}/>
                <NavItem to="/visitors" icon={<Calendar24Regular/>} label={t('appointments')}/>
                <NavItem to="/contacts" icon={<ContactCard24Regular/>} label={t('contacts')}/>
                <NavItem
                    to="/documents"
                    icon={<Document24Regular />}
                    label={
                        <>
                            <span>{t('documents')}</span>
                            {unreadDocuments > 0 && (
                                <Badge appearance="filled" color="danger">
                                    {unreadDocuments}
                                </Badge>
                            )}
                        </>
                    }
                />
            </div>
            
        </nav>
    );
}
